import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { isAddress } from 'viem'

import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgId } from '~/hooks/org'
import { useBatchContactLookup } from '~/hooks/useContacts/contactBatchAddressLookup'
import { useOrgContactsUpdates } from '~/hooks/useOrgContacts'
import { useServerRequests } from '~/hooks/useRequests'
import {
  AddressSearchResult,
  ContactSendRule,
  IAddress,
  OrgContact,
} from '~/types'
import { EvmAddress } from '~/utils/address'

type ContactSearchResult = Pick<
  AddressSearchResult,
  'address' | 'orgLabel' | 'smartAccountName'
>

export const useContactSearch = (queryInput: string) => {
  const { serverGetRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()

  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: [QUERY_KEYS.CONTACT_SEARCH, activeOrgId, queryInput],
    queryFn: async () => {
      if (!activeOrgId) throw new Error('Org ID required')

      const result = await serverGetRequest<{
        results: ContactSearchResult[]
      }>({
        path: `/api/org/contacts/v2`,
        params: {
          query: queryInput,
          orgId: activeOrgId,
        },
      })

      if (!result?.data) {
        throw new Error('No data returned')
      }

      result.data.results.forEach((result) => {
        if (result.address) {
          queryClient.setQueryData(
            [QUERY_KEYS.CONTACT_LOOKUP, activeOrgId, result.address],
            result,
          )
        }
      })

      return result.data.results
    },
    enabled: !!queryInput,
  })

  return { results: query.data, isLoading: query.isLoading }
}

export function useContactLookup(address: IAddress | undefined) {
  const activeOrgId = useActiveOrgId()
  const {
    addressSearchHistory: { searchHistory },
  } = useAuth()

  const batchLookup = useBatchContactLookup()

  const query = useQuery({
    queryKey: [QUERY_KEYS.CONTACT_LOOKUP, activeOrgId, address],
    queryFn: async () => {
      if (!activeOrgId) throw new Error('Org ID required')
      if (!address) throw new Error('No address provided')
      if (!batchLookup) throw new Error('Batch lookup not initialized')

      const result = await batchLookup.call({ address, orgId: activeOrgId })
      if (!result) {
        throw new Error('No data returned')
      }

      return result
    },
    placeholderData: () => {
      const initialData = searchHistory.find(
        (result) =>
          result.address === address &&
          (result.orgLabel || result.smartAccountName),
      )
      return initialData
    },
    enabled: !!address && isAddress(address) && !!activeOrgId && !!batchLookup,
  })

  return query
}

export function useUpsertContact(address: IAddress) {
  const queryClient = useQueryClient()
  const { serverPostRequest, isProcessing } = useServerRequests()
  const { upsertContact: cacheContact } = useOrgContactsUpdates()
  const { data: contact } = useContactLookup(address)
  const activeOrgId = useActiveOrgId()

  const canAddContact = !contact?.smartAccountName

  const upsertContact = async ({
    label,
    sendRules,
    onSuccess,
    successMessage,
  }: {
    label: string
    // Omit to keep the rules the contact already has, which a rename needs.
    sendRules?: ContactSendRule[]
    onSuccess?: () => void
    successMessage?: string
  }) => {
    if (!EvmAddress.validate(address)) {
      toast.error('Only EVM addresses can be added as contacts')
      return
    }

    if (!canAddContact) {
      toast.error('Cannot add smart account as a contact')
      return
    }

    const result = await serverPostRequest<{ contact: OrgContact }>({
      path: '/api/org/contacts/v2',
      params: {
        orgId: activeOrgId,
        address,
        label,
        ...(sendRules ? { sendRules } : {}),
      },
      errorMessage: 'Failed to save contact',
    })

    if (!result?.data?.contact) return

    toast.success(
      successMessage ??
        (contact?.orgLabel ? 'Contact updated' : 'Contact added'),
    )

    cacheContact(result.data.contact)
    // Update cache before exiting edit mode to prevent stale name flash
    queryClient.setQueryData(
      [QUERY_KEYS.CONTACT_LOOKUP, activeOrgId, address],
      (old: ContactSearchResult | undefined) =>
        old
          ? { ...old, orgLabel: label }
          : { address, orgLabel: label, smartAccountName: undefined },
    )

    onSuccess?.()

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.CONTACT_LOOKUP, activeOrgId, address],
    })

    return result.data.contact
  }

  return {
    upsertContact,
    isProcessing,
    canAddContact,
  }
}

export function useRemoveContact(address: IAddress) {
  const queryClient = useQueryClient()
  const activeOrgId = useActiveOrgId()
  const { serverPostRequest, isProcessing } = useServerRequests()
  const { removeContact: uncacheContact } = useOrgContactsUpdates()
  const {
    addressSearchHistory: { removeFromSearchHistory },
  } = useAuth()
  const { data: contact } = useContactLookup(address)

  const removeContact = async (onSuccess?: () => void) => {
    if (!contact?.orgLabel) {
      throw new Error('No contact to remove')
    }

    const result = await serverPostRequest({
      path: '/api/org/contacts/v2/remove',
      params: { orgId: activeOrgId, address },
      errorMessage: 'Failed to remove contact',
    })

    if (!result) return

    toast.success('Contact removed')
    removeFromSearchHistory(address)
    uncacheContact(address)

    // Update cache before exiting edit mode to prevent stale name flash
    queryClient.setQueryData(
      [QUERY_KEYS.CONTACT_LOOKUP, activeOrgId, address],
      (old: ContactSearchResult | undefined) =>
        old ? { ...old, orgLabel: undefined } : undefined,
    )

    onSuccess?.()

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.CONTACT_LOOKUP, activeOrgId, address],
    })

    return result
  }

  return {
    removeContact,
    isProcessing,
  }
}
